import {
  FormControlLabel,
  FormControl,
  Grid,
  InputAdornment,
  Radio,
  Switch,
  Typography,
  MenuItem,
  RadioGroup,
} from "@mui/material";
import { Field, useFormikContext } from "formik";
import FormikTextField from "../../../../components/common/formikFields/FormikTextField";
import { UpdateRouteRateUIRequest } from "./UpdateRouteRateModal";
import { DISOUNT_RATE_TYPES } from "../RateCardConstants";
import { mapSnakeCaseToCapitalCaseWithSpaces } from "../../../../components/common/utils/StringUtils";
import { useEffect } from "react";

interface Props {
  values: UpdateRouteRateUIRequest;
  handleChange: any;
  handleBlur: any;
}

const DiscountPremium = (props: Props) => {
  const { values, handleChange, handleBlur } = props;
  const { setFieldValue } = useFormikContext<UpdateRouteRateUIRequest>();

  // Disable the discount fields when the "rate" type is selected
  const isRateSelected = values.discount_type === "rate";
  const isVolumeSelected = values.discount_type === "volume";

  // Reset fields when discount_type changes
  useEffect(() => {
    if (isRateSelected) {
      setFieldValue("discount_volume_min_unit", "");
      setFieldValue("discount_volume_frequency", "");
      setFieldValue("discount_volume_unit_type", "");
      setFieldValue("discount_volume_value_percentage", "");
      setFieldValue("discount_volume_value_dollar", "");
    }

    if (isVolumeSelected) {
      setFieldValue("discount_rate_type", "");
      setFieldValue("discount_rate_min_unit", "");
      setFieldValue("discount_rate_frequency", "");
      setFieldValue("discount_rate_unit_type", "");
      setFieldValue("discount_rate_value_percentage", "");
      setFieldValue("discount_rate_value_dollar", "");
    }
  }, [values.discount_type, isRateSelected, isVolumeSelected, setFieldValue]);

  return (
    <>
      <FormControlLabel
        id="show_discount"
        name="show_discount"
        label="Discount Premium"
        sx={{ mt: 2 }}
        labelPlacement="start"
        checked={values.show_discount}
        control={
          <Field
            id="show_discount"
            name="show_discount"
            component={Switch}
            type="checkbox"
           // size="small"
            onChange={handleChange}
            onBlur={handleBlur}
          />
        }
      />
      <Grid container item alignItems={"center"}>
        <RadioGroup
          id="discount_type"
          name="discount_type"
          value={values.discount_type}
          onChange={handleChange}
          onBlur={handleBlur}
          sx={{ display: values.show_discount ? "block" : "none" }}
        >
          {/* First Discount statement - Volume */}
          <FormControlLabel
            control={<Radio />}
            value="volume"
            label={
              <Grid container item alignItems={"center"} spacing={1}>
                <Grid item>
                  <Typography>If volume greater than </Typography>
                </Grid>
                <Grid item>
                  <FormikTextField
                    margin="normal"
                    id="discount_volume_min_unit"
                    name="discount_volume_min_unit"
                    label="Volume (Units)"
                    placeholder="Units"
                    size="small"
                    variant="outlined"
                    sx={{ width: 70 }}
                    disabled={isRateSelected} // Disable if "rate" is selected
                  />
                </Grid>
                <Grid item>
                  <Typography>over </Typography>
                </Grid>
                <Grid item>
                  <FormikTextField
                    select
                    margin="normal"
                    id="discount_volume_frequency"
                    name="discount_volume_frequency"
                    label="Frequency"
                    size="small"
                    sx={{ width: 110 }}
                    variant="outlined"
                    disabled={isRateSelected} // Disable if "rate" is selected
                  >
                    {["weekly", "bi-weekly", "monthly"].map((timePeriod, index) => (
                      <MenuItem key={index} value={timePeriod}>
                        {timePeriod}
                      </MenuItem>
                    ))}
                  </FormikTextField>
                </Grid>
                <Grid item>
                  <Typography>then discount equals to </Typography>
                </Grid>
                <Grid item>
                  <FormControl disabled={isRateSelected}> {/* Wrap in FormControl */}
                    <RadioGroup
                      row
                      id="discount_volume_unit_type"
                      name="discount_volume_unit_type"
                      value={values.discount_volume_unit_type}
                      onChange={handleChange}
                    >
                      <FormControlLabel
                        control={<Radio />}
                        value="percentage"
                        label={
                          <FormikTextField
                            id="discount_volume_value_percentage"
                            name="discount_volume_value_percentage"
                            variant="outlined"
                            size="small"
                            label="Percentage"
                            sx={{ width: 80 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">%</InputAdornment>
                              ),
                            }}
                            disabled={isRateSelected} // Disable if "rate" is selected
                          />
                        }
                      />
                      <FormControlLabel
                        control={<Radio />}
                        value="value"
                        label={
                          <FormikTextField
                            id="discount_volume_value_dollar"
                            name="discount_volume_value_dollar"
                            variant="outlined"
                            size="small"
                            label="Dollar Value"
                            sx={{ width: 80 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">$</InputAdornment>
                              ),
                            }}
                            disabled={isRateSelected} // Disable if "rate" is selected
                          />
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            }
          />
          {/* Second Discount statement - Rate */}
          <FormControlLabel
            control={<Radio />}
            value="rate"
            label={
              <Grid container item alignItems={"center"} spacing={1}>
                <Grid item>
                  <Typography>If </Typography>
                </Grid>
                <Grid item>
                  <FormikTextField
                    select
                    margin="normal"
                    id="discount_rate_type"
                    name="discount_rate_type"
                    label="Rate Type"
                    size="small"
                    sx={{ width: 140 }}
                    variant="outlined"
                    disabled={isVolumeSelected} // Disable if "volume" is selected
                  >
                    {DISOUNT_RATE_TYPES.map((rateType, index) => (
                      <MenuItem
                        key={index}
                        value={rateType}
                        style={{ textTransform: "capitalize" }}
                      >
                        {mapSnakeCaseToCapitalCaseWithSpaces(rateType)}
                      </MenuItem>
                    ))}
                  </FormikTextField>
                </Grid>
                <Grid item>
                  <Typography>greater than </Typography>
                </Grid>
                <Grid item>
                  <FormikTextField
                    margin="normal"
                    id="discount_rate_min_unit"
                    name="discount_rate_min_unit"
                    label="Dollar Amount"
                    placeholder="Dollars"
                    size="small"
                    variant="outlined"
                    sx={{ width: 70 }}
                    disabled={isVolumeSelected} // Disable if "volume" is selected
                  />
                </Grid>
                <Grid item>
                  <Typography>over </Typography>
                </Grid>
                <Grid item>
                  <FormikTextField
                    select
                    margin="normal"
                    id="discount_rate_frequency"
                    name="discount_rate_frequency"
                    label="Frequency"
                    size="small"
                    sx={{ width: 110 }}
                    variant="outlined"
                    disabled={isVolumeSelected} // Disable if "volume" is selected
                  >
                    {["weekly", "monthly"].map((timePeriod, index) => (
                      <MenuItem key={index} value={timePeriod}>
                        {timePeriod}
                      </MenuItem>
                    ))}
                  </FormikTextField>
                </Grid>
                <Grid item>
                  <Typography>then discount equals to </Typography>
                </Grid>
                <Grid item>
                  <FormControl disabled={isVolumeSelected}> {/* Wrap in FormControl */}
                    <RadioGroup
                      row
                      id="discount_rate_unit_type"
                      name="discount_rate_unit_type"
                      value={values.discount_rate_unit_type}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <FormControlLabel
                        control={<Radio />}
                        value="percentage"
                        label={
                          <FormikTextField
                            id="discount_rate_value_percentage"
                            name="discount_rate_value_percentage"
                            variant="outlined"
                            label="Percentage"
                            size="small"
                            sx={{ width: 80 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">%</InputAdornment>
                              ),
                            }}
                            disabled={isVolumeSelected} // Disable if "volume" is selected
                          />
                        }
                      />
                      <FormControlLabel
                        control={<Radio />}
                        value="value"
                        label={
                          <FormikTextField
                            id="discount_rate_value_dollar"
                            name="discount_rate_value_dollar"
                            variant="outlined"
                            size="small"
                            label="Dollar Value"
                            sx={{ width: 80 }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">$</InputAdornment>
                              ),
                            }}
                            disabled={isVolumeSelected} // Disable if "volume" is selected
                          />
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            }
          />
        </RadioGroup>
      </Grid>
    </>
  );
};

export default DiscountPremium;
